/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** The coupon field, between the lines and the total. */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text, TextInput, UnstyledButton } from '@/shared/ui';
import {
  isSubmittable, normaliseCode, type AppliedCoupon,
} from './Checkout.coupon';

export interface CouponFieldProps {
  applied: AppliedCoupon | null;
  /** Why the code cannot be checked yet, when it cannot. */
  blockedReason?: string | null;
  /**
   * A code being put to a new price, while it is.
   *
   * A change to the order takes the old discount off and applies the code
   * again; for that moment there is no discount to show, and the field says
   * the code is still there rather than looking emptied.
   */
  pendingCode?: string | null;
  disabled?: boolean;
  /** Asks the server; returns the reason when the code is no good. */
  onApply: (code: string) => Promise<string | null>;
  onRemove: () => void;
}

/**
 * Says the code is being applied again.
 *
 * @param props - The code in flight.
 * @returns The status line.
 */
const PendingNotice: React.FC<{ code: string }> = ({ code }) => {
  const { t } = useTranslation('@pages/onboarding');
  return (
    <Text
      component="span"
      role="status"
      data-testid="checkout-coupon-pending"
      style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}
    >
      {t('checkoutStep.coupon.reapplying', { code })}
    </Text>
  );
};

/**
 * The coupon field.
 *
 * Closed until asked for: most buyers do not have one, and a field for
 * something they lack reads as a discount they are missing out on.
 */
export const CouponField: React.FC<CouponFieldProps> = ({
  applied, blockedReason = null, pendingCode = null, disabled = false, onApply, onRemove,
}) => {
  const { t } = useTranslation('@pages/onboarding');
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (disabled || blockedReason || !isSubmittable(code) || busy) return;
    setBusy(true);
    setError(null);
    try {
      // The reason is said in the checkout's dialog; here it only marks the
      // field, so the summary does not grow a line under the total.
      setError(await onApply(normaliseCode(code)));
    } finally {
      setBusy(false);
    }
  };

  if (applied) {
    return (
      <div
        data-testid="checkout-coupon-applied"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 'var(--space-2)',
        }}
      >
        <Text
          component="span"
          style={{
            fontSize: 'var(--text-small)', fontWeight: 'var(--font-semibold)',
            color: 'var(--status-success-text)',
          }}
        >
          {t('checkoutStep.coupon.applied', { code: applied.code })}
        </Text>
        <UnstyledButton
          disabled={disabled}
          onClick={() => { if (!disabled) { onRemove(); setOpen(false); setCode(''); } }}
          data-testid="checkout-coupon-remove"
          style={{
            background: 'none', cursor: disabled ? 'default' : 'pointer', fontSize: 'var(--text-small)',
            color: 'var(--link-text)', textDecoration: 'underline',
            textUnderlineOffset: 3,
          }}
        >
          {t('checkoutStep.coupon.remove')}
        </UnstyledButton>
      </div>
    );
  }

  if (pendingCode) return <PendingNotice code={pendingCode} />;

  if (!open) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <UnstyledButton
          disabled={disabled}
          onClick={() => { if (!disabled) setOpen(true); }}
          data-testid="checkout-coupon-open"
          style={{
            alignSelf: 'flex-start', background: 'none', cursor: disabled ? 'default' : 'pointer',
            fontSize: 'var(--text-small)', fontWeight: 'var(--font-semibold)',
            color: 'var(--link-text)',
          }}
        >
          {t('checkoutStep.coupon.have')}
        </UnstyledButton>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <TextInput
          value={code}
          disabled={disabled || busy}
          onChange={(event) => { setCode(event.target.value.toUpperCase()); setError(null); }}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') { event.preventDefault(); apply(); }
          }}
          placeholder={t('checkoutStep.coupon.placeholder')}
          aria-label={t('checkoutStep.coupon.placeholder')}
          aria-invalid={error ? true : undefined}
          data-testid="checkout-coupon-input"
          style={{ flex: 1, minWidth: 0 }}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={apply}
          disabled={disabled || Boolean(blockedReason) || !isSubmittable(code) || busy}
          data-testid="checkout-coupon-apply"
        >
          {t('checkoutStep.coupon.apply')}
        </Button>
      </div>
      {/* Said rather than hidden: a buyer holding a code should learn the
          discount exists while they are deciding, not after. */}
      {blockedReason && !error && (
        <Text
          component="span"
          data-testid="checkout-coupon-blocked"
          style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}
        >
          {blockedReason}
        </Text>
      )}
    </div>
  );
};
